'use client';

import { c as _c } from "react/compiler-runtime";
import { getCookies } from 'cookies-next/client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { maxAge, minAge, vulnerableCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.ts';
import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import { ChildAvailabilityItem, ChildAvailabilitySearchResults } from '@sola/schema/dotcom/children/child.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { buildStorageFingerprint, createSessionStorage } from '@sola/shared-utils/session-storage/session-storage.ts';
const defaultLimit = 12;
const maxPages = 9;
const scrollToElement = (childId?: string) => {
  if (!childId) {
    return;
  }
  requestAnimationFrame(() => {
    const childElement = document.querySelector(`.SacCard__bio-link[href*="${childId}"]`);
    childElement?.scrollIntoView({
      behavior: 'instant',
      block: 'start'
    });
  });
};
const resolveFilteredCountries = (countries: string[] | null, isVulnerable: boolean | undefined): string[] | undefined => {
  if (!isVulnerable) {
    return countries ? [...countries] : undefined;
  }
  const hvc = vulnerableCountryIsoCodes as readonly string[];
  const selected = countries ? [...countries] : undefined;
  if (selected?.length) {
    const matched = selected.filter(c => hvc.includes(c));
    // If none of the selected countries are HVC, fall back to the selection
    return matched.length > 0 ? matched : selected;
  }
  return [...vulnerableCountryIsoCodes];
};
export const useSacChildren = (filters, allowShowMore) => {
  const $ = _c(107);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = createSessionStorage();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    get: getFromSessionStorage,
    set: setInSessionStorage,
    compare: compareSessionStorage,
    readChildStore
  } = t0;
  const [page, setPage] = useState(0);
  const [previousPage, setPreviousPage] = useState(0);
  let t1;
  if ($[1] !== filters) {
    t1 = filters ?? {};
    $[1] = filters;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = buildStorageFingerprint(t1);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const filterFingerprint = t2;
  let t3;
  if ($[5] !== filterFingerprint) {
    t3 = name => `${name}:${filterFingerprint}`;
    $[5] = filterFingerprint;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const storageKey = t3;
  let t4;
  if ($[7] !== filters.gender) {
    t4 = filters?.gender ? parseAsString.withDefault(filters?.gender) : parseAsString;
    $[7] = filters.gender;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [gender] = useQueryState("gender", t4);
  let t5;
  if ($[9] !== filters.ageMin) {
    t5 = filters?.ageMin ? parseAsInteger.withDefault(filters?.ageMin) : parseAsInteger;
    $[9] = filters.ageMin;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const [ageMin] = useQueryState("ageMin", t5);
  let t6;
  if ($[11] !== filters.ageMax) {
    t6 = filters?.ageMax ? parseAsInteger.withDefault(filters?.ageMax) : parseAsInteger;
    $[11] = filters.ageMax;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const [ageMax] = useQueryState("ageMax", t6);
  let t7;
  if ($[13] !== filters.countries) {
    t7 = filters?.countries ? parseAsArrayOf(parseAsString).withDefault([...(filters?.countries || "")]) : parseAsArrayOf(parseAsString);
    $[13] = filters.countries;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const [countries] = useQueryState("countries", t7);
  const [birthDay] = useQueryState("birthDay", parseAsString);
  const [birthMonth] = useQueryState("birthMonth", parseAsString);
  const [queryFilters] = useQueryState("filters", parseAsString);
  let t8;
  if ($[15] !== queryFilters) {
    t8 = queryFilters?.includes("hasDisability") || undefined;
    $[15] = queryFilters;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const disability = t8;
  let t9;
  if ($[17] !== queryFilters) {
    t9 = queryFilters?.includes("affectedHiv") || undefined;
    $[17] = queryFilters;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const isInHivAffectedArea = t9;
  let t10;
  if ($[19] !== queryFilters) {
    t10 = queryFilters?.includes("orphanedChild") || undefined;
    $[19] = queryFilters;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const isOrphan = t10;
  let t11;
  if ($[21] !== queryFilters) {
    t11 = queryFilters?.includes("vulnerableExploitation") || undefined;
    $[21] = queryFilters;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const isVulnerable = t11;
  let t12;
  if ($[23] !== countries || $[24] !== isVulnerable) {
    t12 = resolveFilteredCountries(countries, isVulnerable);
    $[23] = countries;
    $[24] = isVulnerable;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const filteredCountries = t12;
  const {
    limit: t13
  } = filters;
  const limit = t13 === undefined ? defaultLimit : t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const [storedChildren, setStoredChildren] = useState(t14);
  const [isLoading, setIsLoading] = useState(true);
  const selectedChildRef = useRef(undefined);
  const pathname = usePathname();
  const router = useRouter();
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = getCookies();
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const cookieStore = t15;
  let t16;
  if ($[28] !== pathname || $[29] !== router) {
    t16 = () => router.push(pathname);
    $[28] = pathname;
    $[29] = router;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const resetFilters = t16;
  const params = useSearchParams();
  let t17;
  if ($[31] !== limit || $[32] !== page || $[33] !== storedChildren) {
    t17 = storedChildren.slice(0, (page + 1) * limit);
    $[31] = limit;
    $[32] = page;
    $[33] = storedChildren;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const children = t17;
  const showLoadMoreButton = storedChildren.length > 0 && page < maxPages && storedChildren.length > (page + 1) * limit;
  let t18;
  if ($[35] !== ageMax || $[36] !== ageMin || $[37] !== birthDay || $[38] !== birthMonth || $[39] !== countries?.length || $[40] !== disability || $[41] !== gender || $[42] !== isInHivAffectedArea || $[43] !== isOrphan || $[44] !== isVulnerable) {
    t18 = () => !!(gender || (ageMin && ageMax ? ageMin !== minAge || ageMax !== maxAge : ageMin || ageMax) || countries?.length || birthDay || birthMonth || isInHivAffectedArea || disability || isOrphan || isVulnerable);
    $[35] = ageMax;
    $[36] = ageMin;
    $[37] = birthDay;
    $[38] = birthMonth;
    $[39] = countries?.length;
    $[40] = disability;
    $[41] = gender;
    $[42] = isInHivAffectedArea;
    $[43] = isOrphan;
    $[44] = isVulnerable;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const hasCriteria = t18;
  let t19;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = (currentPage, results) => {
      setStoredChildren(results?.children as ChildAvailabilityItem[] || []);
      setPage(currentPage);
      setIsLoading(false);
    };
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const storeResults = t19;
  let t20;
  if ($[47] !== storageKey) {
    t20 = results_0 => {
      const raw = getFromSessionStorage(storageKey("sacPage"));
      const parsed = Number(raw);
      const restoredPage = Number.isNaN(parsed) ? 0 : parsed;
      selectedChildRef.current = sessionStorage.getItem("sacSelectedChild") ?? undefined;
      sessionStorage.removeItem("sacSelectedChild");
      setPreviousPage(restoredPage);
      storeResults(restoredPage, results_0);
    };
    $[47] = storageKey;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const restorePreviousResults = t20;
  let t21;
  if ($[49] !== params || $[50] !== storageKey) {
    t21 = (currentPage_0, results_1) => {
      setInSessionStorage([storageKey("sacChildren"), storageKey("sacRef"), storageKey("sacParams"), storageKey("sacPage")], [JSON.stringify(results_1), String(cookieStore?.referrer), params.toString(), String(currentPage_0)]);
      storeResults(currentPage_0, results_1);
    };
    $[49] = params;
    $[50] = storageKey;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const saveVisit = t21;
  let t22;
  if ($[52] !== storageKey) {
    t22 = newParams => compareSessionStorage([storageKey("sacRef"), storageKey("sacParams")], [String(cookieStore?.referrer), newParams]) && getFromSessionStorage(storageKey("sacChildren")) !== null;
    $[52] = storageKey;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const visitedPreviously = t22;
  let t23;
  if ($[54] !== page || $[55] !== storageKey) {
    t23 = () => {
      if (page < maxPages) {
        setPreviousPage(page);
        setPage(page + 1);
        setInSessionStorage(storageKey("sacPage"), String(page + 1));
      }
    };
    $[54] = page;
    $[55] = storageKey;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  const incrementPage = t23;
  let t24;
  if ($[57] !== ageMax || $[58] !== ageMin || $[59] !== birthDay || $[60] !== birthMonth || $[61] !== countries || $[62] !== disability || $[63] !== filteredCountries || $[64] !== gender || $[65] !== hasCriteria || $[66] !== isInHivAffectedArea || $[67] !== isOrphan || $[68] !== isVulnerable || $[69] !== limit || $[70] !== page || $[71] !== params || $[72] !== previousPage || $[73] !== restorePreviousResults || $[74] !== saveVisit || $[75] !== storageKey || $[76] !== visitedPreviously) {
    t24 = () => {
      let cancelled = false;
      const fetchData = async () => {
        const cached = readChildStore(storageKey("sacChildren"));
        if (cached && visitedPreviously(params.toString())) {
          if (!cancelled) {
            restorePreviousResults(cached);
          }
          return;
        }
        setStoredChildren([]);
        setIsLoading(true);
        let reset = false;
        if (previousPage === 0 && page > 0) {
          setPreviousPage(page);
        } else {
          setPage(0);
          reset = true;
        }
        const results_2 = hasCriteria() ? await childSearch({
          take: limit * 10,
          skip: 0,
          gender: gender as ChildGenderEnum,
          ageMin,
          ageMax,
          countries: isVulnerable ? filteredCountries : countries,
          birthDay,
          birthMonth,
          isOrphan,
          isInHivAffectedArea,
          disability,
          referrer: cookieStore?.referrer
        }) : await getRandomChildren(limit * 10);
        if (!cancelled) {
          saveVisit(reset ? 0 : page, results_2);
        }
      };
      fetchData();
      return () => {
        cancelled = true;
      };
    };
    $[57] = ageMax;
    $[58] = ageMin;
    $[59] = birthDay;
    $[60] = birthMonth;
    $[61] = countries;
    $[62] = disability;
    $[63] = filteredCountries;
    $[64] = gender;
    $[65] = hasCriteria;
    $[66] = isInHivAffectedArea;
    $[67] = isOrphan;
    $[68] = isVulnerable;
    $[69] = limit;
    $[70] = page;
    $[71] = params;
    $[72] = previousPage;
    $[73] = restorePreviousResults;
    $[74] = saveVisit;
    $[75] = storageKey;
    $[76] = visitedPreviously;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== ageMax || $[79] !== ageMin || $[80] !== birthDay || $[81] !== birthMonth || $[82] !== countries || $[83] !== disability || $[84] !== filteredCountries || $[85] !== gender || $[86] !== isInHivAffectedArea || $[87] !== isOrphan || $[88] !== isVulnerable || $[89] !== limit || $[90] !== params) {
    t25 = [gender, ageMax, ageMin, countries, filteredCountries, disability, isInHivAffectedArea, isOrphan, isVulnerable, birthDay, birthMonth, limit, params];
    $[78] = ageMax;
    $[79] = ageMin;
    $[80] = birthDay;
    $[81] = birthMonth;
    $[82] = countries;
    $[83] = disability;
    $[84] = filteredCountries;
    $[85] = gender;
    $[86] = isInHivAffectedArea;
    $[87] = isOrphan;
    $[88] = isVulnerable;
    $[89] = limit;
    $[90] = params;
    $[91] = t25;
  } else {
    t25 = $[91];
  }
  useEffect(t24, t25);
  let t26;
  if ($[92] !== children?.length || $[93] !== isLoading) {
    t26 = () => {
      if (!isLoading && selectedChildRef.current && children?.length) {
        scrollToElement(selectedChildRef.current);
        selectedChildRef.current = undefined;
      }
    };
    $[92] = children?.length;
    $[93] = isLoading;
    $[94] = t26;
  } else {
    t26 = $[94];
  }
  let t27;
  if ($[95] !== children || $[96] !== isLoading) {
    t27 = [isLoading, children];
    $[95] = children;
    $[96] = isLoading;
    $[97] = t27;
  } else {
    t27 = $[97];
  }
  useEffect(t26, t27);
  let t28;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = [];
    $[98] = t28;
  } else {
    t28 = $[98];
  }
  useEffect(_temp, t28);
  const t29 = !hasCriteria();
  const t30 = allowShowMore && showLoadMoreButton;
  let t31;
  if ($[99] !== children || $[100] !== incrementPage || $[101] !== isLoading || $[102] !== limit || $[103] !== resetFilters || $[104] !== t29 || $[105] !== t30) {
    t31 = {
      isLoading,
      children,
      showRichMix: t29,
      showLoadMoreButton: t30,
      incrementPage,
      resetFilters,
      limit
    };
    $[99] = children;
    $[100] = incrementPage;
    $[101] = isLoading;
    $[102] = limit;
    $[103] = resetFilters;
    $[104] = t29;
    $[105] = t30;
    $[106] = t31;
  } else {
    t31 = $[106];
  }
  return t31;
};
function _temp() {
  if (globalThis.window !== undefined && globalThis.history && "scrollRestoration" in globalThis.history) {
    globalThis.history.scrollRestoration = "manual";
  }
}